import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EditTwoTone } from "@ant-design/icons";
import {
  Button,
  Col,
  Descriptions,
  Divider,
  Modal,
  notification,
  Popover,
  Row,
  Typography,
} from "antd";
import { isNil } from "lodash";

import DataTable from "components/elements/DataTable";
import {
  ColumnType,
  FiltersValues,
} from "components/elements/DataTable/DataTable";

import {
  useDeleteThMutation,
  useGetLandwirtMasterdataForTreuhaenderQuery,
  useGetTreuhaenderByIdQuery,
  useGetTreuhaenderQuery,
  useGetTreuhandstelleByIdQuery,
  useGetTreuhandstelleQuery,
} from "store/masterdata";
import { LandwirtMasterdata } from "types/masterdata.types";
import useEnumsFilters from "hooks/useEnumsFilters";
import { NumberParam, useQueryParams } from "use-query-params";
import getAuthority from "utils/getAuthority";
import { Roles } from "types/user.types";
import ConfirmationModal from "views/App/components/Modals/ConfirmationModal";
import UpdateTreuhanderForm from "./Forms/UpdateTreuhanderForm";
import styles from "./THInfoModal.module.less";

interface Props {
  kundennummer: number;
  treuhaenderId: number;
  treuhandstelleId?: number | undefined | null;
  rekStatusFilters: FiltersValues;
  visible: boolean;
  onClose(clearQuery: boolean): void;
  tHsInfoModalOnClose?: () => void;
}

const { Title } = Typography;

function THInfoModal({
  kundennummer,
  treuhaenderId,
  treuhandstelleId,
  rekStatusFilters,
  visible,
  onClose,
  tHsInfoModalOnClose,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [, setQuery] = useQueryParams({
    kundennummer: NumberParam,
  });
  const [treuhanderFormVisible, setTreuhanderFormVisible] = useState(false);

  const roles = getAuthority();

  const actionsVisible = roles.some(
    (role) =>
      role === Roles.LinkPL ||
      role === Roles.LinkFeldleitung ||
      role === Roles.LinkAdmin
  );

  const [
    deleteThConfirmationModalVisible,
    setDeleteThConfirmationModalVisible,
  ] = useState(false);
  const [deleteTh] = useDeleteThMutation();

  const openDeleteThConfirmationModal = () => {
    setDeleteThConfirmationModalVisible(true);
  };

  function getTreuhandstelle() {
    if (treuhandstelleId && treuhandstelleId > 0) {
      return useGetTreuhandstelleByIdQuery({
        treuhandstelleId,
      });
    }

    return useGetTreuhandstelleQuery(
      {
        kundennummer,
      },
      {
        skip: kundennummer === -1,
      }
    );
  }

  const { data: treuhandstelleData } = getTreuhandstelle();

  function getTreuhaender() {
    if (treuhaenderId > 0) {
      return useGetTreuhaenderByIdQuery({
        treuhaenderId,
      });
    }

    return useGetTreuhaenderQuery(
      {
        kundennummer,
      },
      {
        skip: kundennummer === -1,
      }
    );
  }

  const { data: treuhaenderData, refetch: refetchTreuhaender } =
    getTreuhaender();

  const { data, isLoading, isFetching, refetch } =
    useGetLandwirtMasterdataForTreuhaenderQuery(
      {
        treuhaenderId,
      },
      {
        skip: treuhaenderId === -1,
      }
    );

  const confirmDeleteTh = async () => {
    try {
      const response = await deleteTh({
        treuhaenderId,
      }).unwrap();
      if (!response.hasErrors) {
        onClose(true);
        notification.success({
          message: t("data_control.delete_th_success"),
        });
      } else {
        notification.error({ message: response.errorMessage });
      }
    } catch (error) {
      console.error("Error", error);
    } finally {
      setDeleteThConfirmationModalVisible(false);
    }
  };

  const openTreuhanderModal = () => {
    setTreuhanderFormVisible(true);
  };

  const { getOnFilter } = useEnumsFilters<
    LandwirtMasterdata & { id: number }
  >();

  const goToLWDetailsModal = (rowKundennummer: number) => {
    setQuery({ kundennummer: rowKundennummer });
    onClose(false);
    tHsInfoModalOnClose?.();
  };

  return (
    <Modal
      title={
        <Title level={4} className={styles.title}>
          {`TH-Nummer ${treuhaenderId} Information`}
        </Title>
      }
      visible={visible}
      footer={null}
      onCancel={() => onClose(true)}
      destroyOnClose
      width={1300}
    >
      <Row gutter={[10, 10]}>
        {actionsVisible && (
          <Col className="gutter-row">
            <Button
              type="primary"
              onClick={openDeleteThConfirmationModal}
              disabled={data?.some((x) => x.kundennummer > 0)}
            >
              {t("data_control.delete_th")}
            </Button>
          </Col>
        )}
      </Row>
      <Row style={{ marginTop: 20 }}>
        <Col span={24}>
          <Row>
            <Col span={12}>
              <Descriptions
                layout="vertical"
                title={
                  <Row align="middle" gutter={20}>
                    <Col>
                      <span className="ant-descriptions-title">
                        {t("masterdata.lw_details.treuhaender")}
                      </span>
                    </Col>
                    <Col>
                      <Popover
                        content={t(
                          "masterdata.lw_details.edit_treuhaender_infos"
                        )}
                      >
                        <Button
                          type="ghost"
                          icon={<EditTwoTone twoToneColor="#e30613" />}
                          onClick={openTreuhanderModal}
                        />
                      </Popover>
                    </Col>
                  </Row>
                }
              >
                <Descriptions.Item
                  label={t("masterdata.lw_details.id_treuhander")}
                >
                  {treuhaenderId}
                </Descriptions.Item>
                <Descriptions.Item label={t("masterdata.lw_details.lastname")}>
                  {treuhaenderData?.lastname}
                </Descriptions.Item>
                <Descriptions.Item label={t("masterdata.lw_details.firstname")}>
                  {treuhaenderData?.firstname}
                </Descriptions.Item>
                <Descriptions.Item label={t("masterdata.lw_details.email")}>
                  {treuhaenderData?.email || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("masterdata.lw_details.phone")}>
                  {treuhaenderData?.primaryPhone || "-"}
                  <br />
                  {treuhaenderData?.phone2 || "-"}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("masterdata.lw_details.language_treuhander")}
                >
                  {!isNil(treuhaenderData?.language) &&
                    t(
                      `masterdata.enums.LanguageEnum.${treuhaenderData?.language}`
                    )}
                </Descriptions.Item>
                <Descriptions.Item label={t("masterdata.lw_details.address")}>
                  {treuhaenderData?.address || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("masterdata.lw_details.ort")}>
                  {treuhaenderData?.postalCode} {treuhaenderData?.place}
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col span={12}>
              <Descriptions title="Treuhandstelle" layout="vertical">
                <Descriptions.Item label={t("masterdata.lw_details.name")}>
                  {treuhandstelleData?.name}
                </Descriptions.Item>
                <Descriptions.Item label={t("masterdata.lw_details.address")}>
                  {treuhandstelleData?.address}
                  {treuhandstelleData?.addressAddition}
                </Descriptions.Item>
                <Descriptions.Item label={t("masterdata.lw_details.ort")}>
                  {treuhandstelleData?.postalCode} {treuhandstelleData?.place}
                </Descriptions.Item>
                <Descriptions.Item label={t("masterdata.lw_details.email")}>
                  {treuhandstelleData?.email || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("masterdata.lw_details.phone")}>
                  {treuhandstelleData?.phone || "-"}
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        </Col>
        <Divider />
        <Col span={24}>
          <DataTable<LandwirtMasterdata & { id: number }>
            title={<p className="ant-descriptions-title">Zugeteilte LW</p>}
            isLoading={isLoading || isFetching}
            actions={[]}
            headers={[
              {
                title: t("masterdata.table.labels.landwirtId"),
                key: "landwirtId",
                type: ColumnType.Text,
                withSearchFilter: true,
                width: "10%",
                onCell: (row) => ({
                  onClick: () => goToLWDetailsModal(row.kundennummer),
                }),
              },
              {
                title: t("masterdata.table.labels.firstname"),
                key: "firstname",
                type: ColumnType.Text,
                withSearchFilter: true,
                width: "10%",
                onCell: (row) => ({
                  onClick: () => goToLWDetailsModal(row.kundennummer),
                }),
              },
              {
                title: t("masterdata.table.labels.lastname"),
                key: "lastname",
                type: ColumnType.Text,
                withSearchFilter: true,
                width: "10%",
                onCell: (row) => ({
                  onClick: () => goToLWDetailsModal(row.kundennummer),
                }),
              },
              {
                title: t("masterdata.table.labels.kundennummer"),
                key: "kundennummer",
                type: ColumnType.Text,
                withSearchFilter: true,
                width: "10%",
                onCell: (row) => ({
                  onClick: () => goToLWDetailsModal(row.kundennummer),
                }),
              },
              {
                title: t("masterdata.table.labels.rekStatus"),
                key: "rekStatus",
                type: ColumnType.Text,
                customFormater: (value) =>
                  t(`masterdata.enums.RekStatusEnum.${value}`),
                onFilter: getOnFilter("rekStatus"),
                filters: rekStatusFilters,
                width: "10%",
              },
              {
                title: t("masterdata.table.labels.dlStatus"),
                key: "dlStatus",
                type: ColumnType.Text,
                withSearchFilter: true,
                width: "10%",
              },
              {
                title: t("masterdata.table.labels.entschaedigung"),
                key: "entschaedigung",
                type: ColumnType.Boolean,
                width: "10%",
              },
              {
                title: t("masterdata.table.labels.brm"),
                key: "brm",
                type: ColumnType.Boolean,
              },
            ]}
            data={data?.map((item, id) => ({ ...item, id }))}
            isHorizontalScrollDisabled
          />
        </Col>
        {treuhaenderData && (
          <UpdateTreuhanderForm
            initialValues={{
              id: treuhaenderId,
              firstname: treuhaenderData.firstname,
              lastname: treuhaenderData.lastname,
              email: treuhaenderData.email,
              primaryPhone: treuhaenderData.primaryPhone,
              phone2: treuhaenderData.phone2,
              language: treuhaenderData.language,
              address: treuhaenderData.address,
              place: treuhaenderData.place,
              postalCode: treuhaenderData.postalCode,
            }}
            visible={treuhanderFormVisible}
            onClose={() => setTreuhanderFormVisible(false)}
            onSuccess={() => {
              refetchTreuhaender();
              refetch();
            }}
          />
        )}
      </Row>
      <ConfirmationModal
        visible={deleteThConfirmationModalVisible}
        isLoading={isLoading}
        title={t("data_control.delete_th_confirmation_title")}
        description={t("data_control.delete_th_confirmation_text")}
        onClose={() => setDeleteThConfirmationModalVisible(false)}
        onConfirm={() => confirmDeleteTh()}
      />
    </Modal>
  );
}

export default THInfoModal;
